import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';

const STATUSES = ['new','contacted','viewing','applied','approved','rejected','converted','lost'];
const statusColor = s => ({
    new:'bg-gray-100', contacted:'bg-blue-100', viewing:'bg-purple-100',
    applied:'bg-yellow-100', approved:'bg-green-100', rejected:'bg-red-100',
    converted:'bg-green-200', lost:'bg-gray-200',
}[s] || 'bg-gray-100');

const Leads = () => {
    const [leads, setLeads] = useState([]);
    const [properties, setProperties] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const load = async () => {
        try {
            const [l, p] = await Promise.all([
                axios.get('/api/leads'),
                axios.get('/api/properties').catch(() => ({ data: { properties: [] } })),
            ]);
            setLeads(l.data.leads || []);
            setProperties(p.data.properties || []);
        } catch { toast.error('Failed'); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try { await axios.put(`/api/leads/${id}`, { status }); load(); }
        catch { toast.error('Failed'); }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div><h1 className="text-2xl font-bold">Leads &amp; applications</h1>
                    <p className="text-gray-600">Prospects considering your properties.</p></div>
                <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
                    <Plus className="h-4 w-4" /> New lead
                </button>
            </div>
            <div className="bg-white rounded-xl border">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left"><tr>
                        <th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Property</th>
                        <th className="p-3">Source</th><th className="p-3">Status</th><th className="p-3">Follow-up</th>
                    </tr></thead>
                    <tbody>{leads.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-gray-500">No leads yet.</td></tr> :
                        leads.map(l => (
                            <tr key={l.id} className="border-t">
                                <td className="p-3">{l.name}</td>
                                <td className="p-3">{l.phone || '—'}</td>
                                <td className="p-3">{l.property_name || '—'}</td>
                                <td className="p-3 capitalize">{l.source}</td>
                                <td className="p-3">
                                    <select value={l.status} onChange={e => updateStatus(l.id, e.target.value)}
                                            className={`text-xs px-2 py-1 rounded ${statusColor(l.status)}`}>
                                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="p-3">{l.next_follow_up_at ? new Date(l.next_follow_up_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                            </tr>
                        ))}</tbody>
                </table>
            </div>
            {showForm && <NewLead properties={properties} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
        </div>
    );
};

const NewLead = ({ properties, onClose, onDone }) => {
    const [f, setF] = useState({ name:'', phone:'', email:'', property_id:'', source:'walk_in', notes:'' });
    const submit = async e => {
        e.preventDefault();
        try { await axios.post('/api/leads', f); toast.success('Added'); onDone(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded-xl max-w-md w-full">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">New lead</h2><button type="button" onClick={onClose}>✕</button></div>
                <div className="p-4 space-y-2">
                    <input required placeholder="Full name" className="w-full border rounded-lg px-3 py-2" value={f.name} onChange={e => setF({...f, name:e.target.value})} />
                    <div className="grid grid-cols-2 gap-2">
                        <input placeholder="Phone" className="border rounded-lg px-3 py-2" value={f.phone} onChange={e => setF({...f, phone:e.target.value})} />
                        <input type="email" placeholder="Email" className="border rounded-lg px-3 py-2" value={f.email} onChange={e => setF({...f, email:e.target.value})} />
                    </div>
                    <select className="w-full border rounded-lg px-3 py-2" value={f.property_id} onChange={e => setF({...f, property_id:e.target.value})}>
                        <option value="">Interested property…</option>
                        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <select className="w-full border rounded-lg px-3 py-2" value={f.source} onChange={e => setF({...f, source:e.target.value})}>
                        {['walk_in','referral','website','social','agent','other'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <textarea rows={2} placeholder="Notes" className="w-full border rounded-lg px-3 py-2" value={f.notes} onChange={e => setF({...f, notes:e.target.value})} />
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
                    <button className="px-4 py-2 bg-primary-600 text-white rounded-lg">Save</button>
                </div>
            </form>
        </div>
    );
};

export default Leads;
